import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link2, MessageCircle, Mail, Check } from 'lucide-react';
import { Card, Button, Input, useToast } from '@sighthost/ui';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  buildPartnerLinkUrl,
  buildWhatsAppMessage,
  buildEmailBlock,
} from '../lib/partnerLinkMessage';

/**
 * PartnerLinkCard — the campaign link, plus paste-ready messages, on /sales.
 *
 * Sales staff run campaigns from a phone, in WhatsApp. Before this existed the
 * link reached them only by someone pasting it to them out of band, which meant
 * the one artefact the whole attribution model depends on lived in a chat
 * thread. Now they log in and it is on the landing page.
 *
 * The link belongs to the SALES MANAGER (owner direction 2026-08-03). Agents are
 * not part of this feature and see nothing.
 *
 * That rule is enforced by RLS on sales_company, not by anything in here:
 *   sales_company_select_admin        -> admin, every company
 *   sales_company_select_manager_own  -> sales_manager, own company only
 *   no policy for sales_agent         -> filtered to zero rows, no error raised
 *
 * So this component deliberately has NO role check. An agent's query returns no
 * row and the card does not render. Gating in the UI as well would put the rule
 * in two places, and the copy that drifts is always the one in the component.
 *
 * Clipboard is blocked outright in some mobile browsers and in any non-secure
 * context, which is exactly where this will be used. Every action degrades to a
 * selectable input rather than failing silently - same fallback as CopyPayLink.
 */

type CopyKind = 'link' | 'whatsapp' | 'email';

export function PartnerLinkCard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [copied, setCopied] = useState<CopyKind | null>(null);
  const [fallback, setFallback] = useState<string | null>(null);

  const linkQ = useQuery({
    queryKey: ['sales', 'partner-link', user?.id ?? 'anon'],
    enabled: !!user,
    queryFn: async () => {
      // Only the token is selected. recurring_commission_pct lives on this row
      // and must never travel to a browser that has no reason for it.
      const { data, error } = await supabase
        .from('sales_company')
        .select('name, partner_link_token')
        .eq('active', true)
        .not('partner_link_token', 'is', null)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // No link for this user is a normal state, not an error: SightHost's own staff
  // have no campaign link. Render nothing rather than an empty card.
  if (linkQ.isLoading || linkQ.error || !linkQ.data?.partner_link_token) return null;

  const url = buildPartnerLinkUrl(linkQ.data.partner_link_token);
  const senderName = user?.user_metadata?.full_name ?? null;

  async function copy(kind: CopyKind, text: string, label: string) {
    try {
      if (!navigator.clipboard) throw new Error('clipboard_unavailable');
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setFallback(null);
      toast({ tone: 'success', title: 'Copied', body: `${label} copied. Paste it into your chat.` });
      window.setTimeout(() => setCopied((c) => (c === kind ? null : c)), 2000);
    } catch {
      setFallback(text);
      toast({
        tone: 'warning',
        title: 'Clipboard blocked',
        body: 'Your browser blocked clipboard access. Select the text below and copy it manually.',
      });
    }
  }

  function icon(kind: CopyKind, Fallback: typeof Link2) {
    return copied === kind ? <Check className="h-4 w-4" /> : <Fallback className="h-4 w-4" />;
  }

  return (
    <Card className="p-4 sm:p-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">Your campaign link</h2>
        <p className="text-sm text-muted-foreground">
          Send this to prospects. Anyone who requests a quote through it is credited to
          your team automatically. The same link works for everyone here.
        </p>
      </div>

      <Input
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
        aria-label="Your campaign link"
        className="mt-3 font-mono text-xs"
      />

      <div className="mt-3 flex flex-wrap items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => copy('link', url, 'Link')}
          title="Copy just the link"
        >
          {icon('link', Link2)} Copy link
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => copy('whatsapp', buildWhatsAppMessage({ linkUrl: url, senderName }), 'WhatsApp message')}
          title="Copy a ready-to-send WhatsApp message with the link at the end"
        >
          {icon('whatsapp', MessageCircle)} Copy WhatsApp message
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => copy('email', buildEmailBlock({ linkUrl: url, senderName }), 'Email subject and body')}
          title="Copy an email subject line and body with the link"
        >
          {icon('email', Mail)} Copy email message
        </Button>
      </div>

      {fallback !== null && (
        <div className="mt-2 flex flex-col gap-1">
          <Input
            readOnly
            value={fallback}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Message - select and copy manually"
            className="font-mono text-xs"
          />
          <span className="text-xs text-muted-foreground">Tap the box to select all, then copy.</span>
        </div>
      )}
    </Card>
  );
}
